import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type {
  OrgFieldsValues,
  OrgFormValues,
} from 'components/Form/Fields/Org/getOrgFields';
import type {
  FormErrorData,
  FormSubmitFunction,
} from 'components/Form/FormContext';
import type { MultiStepFormValues } from 'components/Form/MultiStepForm/MultiStepFormTypes';
import { useMultiStep } from 'components/Form/MultiStepForm/useMultiStep';
import {
  useUserEnvironment,
  useUserEnvironmentActions,
} from 'contexts/UserEnvironmentContext';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useTrackPageView } from 'hooks/useTrackPageView';
import { useI18n } from 'i18n/hooks/useI18n';
import { I18nLocale } from 'i18n/types/I18nLocale';
import { Layout } from 'layouts/Layout/Layout';
import { LayoutCenteredBody } from 'layouts/Layout/LayoutCenteredBody';
import { apiCreateOrg } from 'modules/listing/orgs/api/listingApiCreateOrg';
import { apiValidateOrg } from 'modules/listing/orgs/api/listingApiValidateOrg';
import { ApiOrgType } from 'modules/listing/orgs/api/types/ApiOrgType';
import type { Nullable } from 'types/Nullable';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LocationType } from 'utils/constants/general/locationType';
import { scrollToTop } from 'utils/scroll';

import { CreateOrgPage } from './CreateOrgPage';

const getValuesToValidate = (
  values: Record<string, unknown>,
  validate: string[],
) =>
  Object.fromEntries(
    Object.entries(values).filter(([k, _v]) => validate.includes(k)),
  );

export function CreateOrgPageContainer() {
  const { i18n, locale } = useI18n();

  const { updateUserPartially } = useUserEnvironmentActions();
  const { user } = useUserEnvironment();

  const navigate = useNavigate();
  const location = useLocation<{ name: string }>();

  const { currentStep, navigateToStep } = useMultiStep();

  useSetDocumentTitle(i18n('Create a New Organization Profile'));

  useTrackPageView('User Dashboard - Create Org', {
    page_type: 'User Dashboard',
  });

  const initialValues = {
    email: '',
    phone: '',
    name: location?.state?.name ?? '',
    orgType: ApiOrgType.NONPROFIT,
    links: [''],
    description: '',
    locale: [I18nLocale.EN, I18nLocale.ES].includes(locale)
      ? locale
      : I18nLocale.EN,
    // no Portuguese for now
    locationType: LocationType.ONSITE,
    address: {
      locationHidden: false,
      country: user?.location.countryCode,
    },
    slideshowItems: [],
    howToGetInvolved: null,
    timeActive: null,
  } satisfies Partial<Nullable<OrgFormValues>>;

  // Additional errors appended to form errors
  const clientSideValidation = useCallback(
    (values: OrgFieldsValues & MultiStepFormValues) => {
      if (currentStep === 1) {
        // make sure we submit links for validation
        if (values.orgType !== ApiOrgType.COMMUNITY) {
          const emailEmpty = !(values.email || '').trim();
          const phoneEmpty = !(values.phone || '').trim();
          const linksEmpty =
            values.links?.filter((link) => link.trim() !== '').length === 0;

          if (emailEmpty && phoneEmpty && linksEmpty) {
            const error = i18n(
              'Required: Please enter at least one email, phone number, or link',
            );
            return { orgContact: error };
          }
        }
      }
    },
    [i18n, currentStep],
  );

  const onSubmit: FormSubmitFunction<OrgFieldsValues & MultiStepFormValues> =
    useCallback(
      ({ values, setErrors, handleError, updateSubmitting }) => {
        if (!user) return;

        const cleanedValues = {
          ...values,
          is501c3: values.address.country === 'US' && values.is501c3,
          links: values.links.filter((link) => link.trim() !== ''),
        };

        const valuesToValidate: Partial<OrgFormValues> = values.validate
          ? getValuesToValidate(cleanedValues, values.validate)
          : {};

        if (values.hasNextStep) {
          let backendErrors: Record<
            keyof OrgFieldsValues,
            FormErrorData<keyof OrgFieldsValues>
          >;

          return apiValidateOrg(valuesToValidate)
            .catch((e) => {
              handleError(e);

              // Track specific errors
              const responseJson = 'responseJson' in e ? e.responseJson : null;
              const errorArray: FormErrorData<keyof OrgFieldsValues>[] =
                responseJson.errors ?? [];

              const errors = errorArray.reduce(
                (acc, errorData) => {
                  const { name } = errorData;
                  const value = name ? values[name] : undefined;

                  const errorMessage = { ...errorData, value };
                  return { ...acc, [name]: errorMessage };
                },
                {} as Record<
                  keyof OrgFieldsValues,
                  FormErrorData<keyof OrgFieldsValues>
                >,
              );

              // Specifically track the 'name duplication' event to see how
              // often it is stopping users from creating orgs
              const errorType = 'name' in errors && errors.name.type;
              const orgIsPublic = errorType && Boolean(errors.name.data?.org);
              trackEvent('Error: Creating Org', {
                error_type: errorType,
                org_is_public: orgIsPublic,
              });

              backendErrors = errors;
            })
            .finally(() => {
              const frontendErrors = clientSideValidation(cleanedValues);

              if (frontendErrors) {
                setErrors(frontendErrors, true);
              }

              if (!backendErrors && !frontendErrors) {
                navigateToStep((prevStep) => prevStep + 1);
              }

              updateSubmitting(false);
            });
        } else {
          return apiCreateOrg(cleanedValues)
            .then((org) => {
              const {
                id,
                name,
                searchApiKey,
                address,
                canPostVolops,
                canPostEvents,
                isApproved,
                isAllowedToPost,
                isFrozen,
                canBuyCredits,
                hasAts,
                orgType,
                logo,
              } = org;

              updateUserPartially({
                permissions: !user.permissions.includes('orgAdmin')
                  ? user.permissions.concat('orgAdmin')
                  : user.permissions,
                orgs: [
                  ...user.orgs,
                  {
                    id,
                    name,
                    usedAts: false,
                    cartItemCount: 0,
                    searchApiKey,
                    addressCountry: address.country,
                    canPostVolops,
                    canPostEvents,
                    isApproved,
                    isAllowedToPost,
                    isFrozen,
                    canBuyCredits,
                    hasAts,
                    orgType,
                    logo,
                    fromVm: false, // We can probably remove this after the merger is complete
                    everHadMembership: false,
                  },
                ],
              });

              trackEvent('Success: Created Org', {
                org_id: org.id,
                org_type: org.orgType,
              });

              navigate(`/admin/${org.id}/preview`, { replace: true });
              scrollToTop();
            })
            .catch((e) => {
              const { errors: errorArray } = e.responseJson;

              if (errorArray) {
                const errors = errorArray.reduce(
                  // oxlint-disable-next-line typescript/no-explicit-any
                  (acc: any, { name, type, description, data }: any) => {
                    // eslint-disable-next-line no-param-reassign
                    acc[name] = {
                      name,
                      type,
                      description,
                      data: data ?? {},
                    };
                    return acc;
                  },
                  {},
                );
                // specifically track the 'name duplication' event to see how
                // often it is stopping users from creating orgs
                const errorType = 'name' in errors && errors.name.type;
                const orgIsPublic = errorType && Boolean(errors.name.data.org);
                trackEvent('Error: Creating Org', {
                  error_type: errorType,
                  org_is_public: orgIsPublic,
                });
              }

              handleError(e);
            });
        }
      },
      [
        user,
        navigate,
        navigateToStep,
        clientSideValidation,
        updateUserPartially,
      ],
    );

  return (
    <Layout variant="base" darkBackground>
      <LayoutCenteredBody>
        <CreateOrgPage initialValues={initialValues} onSubmit={onSubmit} />
      </LayoutCenteredBody>
    </Layout>
  );
}
